import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuditWorkspace } from "./useAuditWorkspace";
import DraftStatus from "./DraftStatus";
import ExitTaskModal from "./ExitTaskModal";
import FieldReviewTable from "./FieldReviewTable";
import Panel from "./Panel";
import MediaGallery from "./MediaGallery";
import ReviewHeader from "./ReviewHeader";
import ChecklistPanel from "./ChecklistPanel";
import { DetailRows } from "./ReviewDetails";
import {
  ReviewUnit,
  fieldValues,
  showsAdditionalView,
  detailRows,
  reviewUnits,
  showsChecklist,
  unitFields,
} from "./mapTaskValues";
import {
  byPriorityThenAge,
  correctionLabel,
  hasCorrectionValue,
  isInQueueTab,
  toQueueTab,
  hasIncorrectDecision,
  isIncorrectDecision,
} from "../shared/helpers";
import {
  wsContainer,
  wsBreadcrumb,
  wsBreadcrumbLink,
  wsBreadcrumbSep,
  wsHeader,
  wsHeaderLeft,
  wsHeaderRight,
  wsNavBtn,
  wsTitle,
  wsBody,
  wsLeftCol,
  wsRightCol,
  wsMediaPlaceholder,
  wsErrorSummary,
  wsSuccessBanner,
} from "./workspaceStyles";
import {
  AuditTask,
  MEDIA_FIELD_TYPES,
  ChecklistItem,
  ChecklistResponseValue,
  FieldCommon,
  FieldDecision,
  FormConfigField,
  MediaUrl,
  ObjectReview,
  UseCaseConfig,
} from "../shared/types";
import * as S from "../shared/styles";
import Loader from "@amzn/meridian/loader";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";

// ----------------------------  Helpers  ----------------------------

function isMediaField(type: string): boolean {
  return (MEDIA_FIELD_TYPES as readonly string[]).indexOf(type) !== -1;
}

/**
 * What still blocks a submit in one set of decisions (the record's, or one
 * object's): a mandatory field with no decision, or an incorrect field without an
 * issue category (or with "Other" but no text). prefix names the object in errors.
 */
function validateFields(
  fields: FormConfigField[],
  decisions: Record<string, FieldDecision>,
  prefix = "",
): string[] {
  const errors: string[] = [];
  fields.forEach((f) => {
    const d = decisions[f.label];
    if (f.mandatory && !d?.decision) {
      errors.push(prefix + f.label);
    } else if (isIncorrectDecision(d?.decision) && !hasCorrectionValue(f, d?.issue_category)) {
      errors.push(prefix + f.label + " (" + correctionLabel(f) + ")");
    }
  });
  return errors;
}

// Stable fallbacks so memo/callback deps don't change on every render.
const EMPTY_FIELD_COMMON: FieldCommon = {};
const EMPTY_DECISIONS: Record<string, FieldDecision> = {};
const EMPTY_CHECKLIST: Record<string, ChecklistResponseValue> = {};
const EMPTY_OBJECT_REVIEWS: Record<string, ObjectReview> = {};

// What one record / object holds of the review: its decisions and checklist answers.
interface UnitReview {
  decisions: Record<string, FieldDecision>;
  checklist: Record<string, ChecklistResponseValue>;
}

interface UnitErrors {
  fields: string[];
  checklist: string[];
}

/**
 * What still blocks a submit on one record / object (the same rules as the
 * backend): its shown fields (none in the additional view), and its checklist where
 * the checklist shows. prefix names the object in errors.
 */
function unitErrors(
  config: UseCaseConfig,
  fields: FormConfigField[],
  checklistItems: ChecklistItem[],
  unit: ReviewUnit,
  review: UnitReview,
  prefix = "",
): UnitErrors {
  return {
    fields: validateFields(unitFields(config, fields, unit), review.decisions, prefix),
    checklist: showsChecklist(config, unit)
      ? checklistItems.filter((i) => !review.checklist[i.id]).map((i) => prefix + i.label)
      : [],
  };
}

const hasErrors = (e: UnitErrors): boolean => e.fields.length > 0 || e.checklist.length > 0;

// ----------------------------  Main Component  ----------------------------

const AuditWorkspace: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  // The queue tab this record was opened from: Prev / Next and exit stay in it.
  const [searchParams] = useSearchParams();
  const tab = toQueueTab(searchParams.get("tab"));
  const queuePath = "/vlmAudit/" + tab;
  const workspacePath = (id: string) => `/vlmAudit/workspace/${id}?tab=${tab}`;

  const {
    task,
    config,
    result,
    allTasks,
    isLoading,
    isError,
    isSaving,
    saveSuccess,
    saveError,
    setDecisions,
    setFieldCommon,
    setChecklist,
    setComments,
    setFlagged,
    setObjectReview,
    saveDraft,
    submitResult,
    resetSave,
  } = useAuditWorkspace(taskId);

  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [dirty, setDirty] = useState(false);
  const [pendingExit, setPendingExit] = useState(false);
  const [objectIndex, setObjectIndex] = useState(0);
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  // Reset per-task UI state when navigating between tasks.
  React.useEffect(() => {
    setAttemptedSubmit(false);
    setExitOpen(false);
    setLastSavedAt(null);
    setDirty(false);
    setPendingExit(false);
    setObjectIndex(0);
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const objectReviews = result?.object_reviews ?? EMPTY_OBJECT_REVIEWS;
  const comments = result?.comments ?? "";
  const flagged = !!result?.is_flagged;
  const submitted = result?.status === "submitted";

  const inputFields = useMemo(
    () =>
      (config?.fields ?? [])
        .filter((f) => !isMediaField(f.type))
        .sort((a, b) => a.display_order - b.display_order),
    [config],
  );

  const mediaFields = useMemo(
    () => (config?.fields ?? []).filter((f) => isMediaField(f.type)),
    [config],
  );

  const checklistItems = useMemo(
    () => (config?.checklist_items ?? []).slice().sort((a, b) => a.display_order - b.display_order),
    [config],
  );

  // What is reviewed: the record, or (object presence) each object on its own, one
  // on screen at a time.
  const units = useMemo(() => (task ? reviewUnits(task) : []), [task]);
  const objectCount = units.length && units[0].objectName !== undefined ? units.length : 0;
  const shownIndex = Math.min(objectIndex, Math.max(units.length - 1, 0));
  const currentUnit: ReviewUnit | undefined = units[shownIndex];
  const currentName = currentUnit?.objectName;

  // The review each unit holds: the record's own, or the object's in object_reviews.
  const reviewOf = useCallback(
    (unit: ReviewUnit): UnitReview => {
      if (unit.objectName === undefined) {
        return {
          decisions: result?.decisions ?? EMPTY_DECISIONS,
          checklist: result?.checklist_responses ?? EMPTY_CHECKLIST,
        };
      }
      const r = objectReviews[unit.objectName];
      return {
        decisions: r?.decisions ?? EMPTY_DECISIONS,
        checklist: r?.checklist_responses ?? EMPTY_CHECKLIST,
      };
    },
    [result?.decisions, result?.checklist_responses, objectReviews],
  );

  // A flag column (Additional View tab) is true: read-only rows, no field rows or decisions.
  const shownAdditional = !!config && !!currentUnit && showsAdditionalView(config, currentUnit);
  // Fields shown: those whose source columns have a value on the record, or on the
  // object shown. Hidden fields are not reviewed (nor required).
  const shownFields = useMemo(
    () => (currentUnit && config ? unitFields(config, inputFields, currentUnit) : []),
    [config, inputFields, currentUnit],
  );
  const shownValues = useMemo(
    () => (currentUnit ? fieldValues(shownFields, currentUnit.source) : {}),
    [shownFields, currentUnit],
  );
  const shownDetails = useMemo(
    () =>
      currentUnit && config && shownAdditional
        ? detailRows(config.additional_view_fields, currentUnit)
        : [],
    [config, currentUnit, shownAdditional],
  );
  const showChecklist = !!config && !!currentUnit && showsChecklist(config, currentUnit);

  // What the form edits: the record's own review, or the shown object's
  // (each object keeps its own decisions, Details and checklist).
  const { decisions, checklist } = currentUnit
    ? reviewOf(currentUnit)
    : { decisions: EMPTY_DECISIONS, checklist: EMPTY_CHECKLIST };
  const fieldCommon =
    currentName !== undefined
      ? objectReviews[currentName]?.field_common ?? EMPTY_FIELD_COMMON
      : result?.field_common ?? EMPTY_FIELD_COMMON;
  const objectNav = objectCount
    ? {
        index: shownIndex,
        total: objectCount,
        onPrev: () => setObjectIndex((i) => (i - 1 + objectCount) % objectCount),
        onNext: () => setObjectIndex((i) => (i + 1) % objectCount),
      }
    : undefined;

  const anyIncorrect = !shownAdditional && hasIncorrectDecision(decisions);
  const errorsOf = useCallback(
    (unit: ReviewUnit, prefix = ""): UnitErrors =>
      config
        ? unitErrors(config, inputFields, checklistItems, unit, reviewOf(unit), prefix)
        : { fields: [], checklist: [] },
    [config, inputFields, checklistItems, reviewOf],
  );
  const { fieldErrors, checklistErrors } = useMemo(() => {
    const all = units.map((u) =>
      errorsOf(u, u.objectName !== undefined ? u.objectName + ": " : ""),
    );
    return {
      fieldErrors: all.reduce<string[]>((acc, e) => acc.concat(e.fields), []),
      checklistErrors: all.reduce<string[]>((acc, e) => acc.concat(e.checklist), []),
    };
  }, [units, errorsOf]);
  const canSubmit = fieldErrors.length === 0 && checklistErrors.length === 0;

  // Images beside the originals, each when there are any: the inspector's override
  // images, and an object's compliance fix images.
  const objectMedia = currentName !== undefined ? task?.object_media?.[currentName] : undefined;
  const sideMedia: { title: string; media: MediaUrl[] }[] = [];
  const override = currentName !== undefined ? objectMedia?.override : task?.override_media_urls;
  if (override?.length) sideMedia.push({ title: "Override images", media: override });
  if (objectMedia?.fix?.length)
    sideMedia.push({ title: "Compliance fix images", media: objectMedia.fix });

  const question = task?.inspection_question?.trim() || null;

  // Last-task detection — scoped to same use case
  const sameCaseTasks = allTasks.filter((t) => t.use_case_id === task?.use_case_id);
  const sameCaseRemaining = sameCaseTasks.filter(
    (t) => t.id !== task?.id && t.status !== "completed" && t.status !== "expired",
  ).length;
  const isLastTask = sameCaseRemaining === 0;
  const recordId = task?.id ?? "";

  // Prev / Next: the same use case's records in the tab this one was opened from,
  // in queue order (wraps around).
  const tabTasks = sameCaseTasks
    .filter((t) => isInQueueTab(t.status, tab))
    .sort((a, b) => byPriorityThenAge(a as AuditTask, b as AuditTask));
  const tabIdx = tabTasks.findIndex((t) => t.id === task?.id);
  const n = tabTasks.length;
  const hasOthers = tabTasks.some((t) => t.id !== task?.id);
  // When this record is no longer in the tab (e.g. just submitted), start from the ends.
  const prevTask = !hasOthers
    ? null
    : tabIdx >= 0
      ? tabTasks[(tabIdx - 1 + n) % n]
      : tabTasks[n - 1];
  const nextTask = !hasOthers ? null : tabIdx >= 0 ? tabTasks[(tabIdx + 1) % n] : tabTasks[0];

  // Handle save success — update last-saved timestamp, or navigate if exiting
  React.useEffect(() => {
    if (saveSuccess && result?.status === "draft") {
      resetSave();
      if (pendingExit) {
        setPendingExit(false);
        navigate(queuePath);
        return;
      }
      setLastSavedAt(new Date());
      setDirty(false);
    }
  }, [saveSuccess, result?.status, resetSave, pendingExit, navigate, queuePath]);

  // Handle submit success — navigate to next task in same use case, or back to queue
  React.useEffect(() => {
    if (saveSuccess && result?.status === "submitted") {
      resetSave();
      const useCaseId = task?.use_case_id;
      // Same order as the queue and the Prev / Next arrows. The submitted record is now
      // completed, so it is located in the full list rather than in the tab's.
      const sameCasePool = allTasks
        .filter((t) => t.use_case_id === useCaseId)
        .sort((a, b) => byPriorityThenAge(a as AuditTask, b as AuditTask));
      const currentIdx = sameCasePool.findIndex((t) => t.id === task?.id);
      const ordered =
        currentIdx >= 0
          ? [...sameCasePool.slice(currentIdx + 1), ...sameCasePool.slice(0, currentIdx)]
          : sameCasePool;
      const next = ordered.find((t) => isInQueueTab(t.status, "my-tasks"));
      if (next) {
        navigate(workspacePath(next.id));
      } else {
        navigate(queuePath);
      }
    }
  }, [
    saveSuccess,
    result?.status,
    allTasks,
    task?.id,
    task?.use_case_id,
    navigate,
    resetSave,
    queuePath,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-dismiss save error after 5 seconds so the banner does not persist indefinitely.
  React.useEffect(() => {
    if (!saveError) return;
    setPendingExit(false);
    const timer = setTimeout(() => resetSave(), 5000);
    return () => clearTimeout(timer);
  }, [saveError, resetSave]);

  // Write the decisions / Details of what is shown: the record, or the current object.
  const writeReview = useCallback(
    (nextDecisions: Record<string, FieldDecision>, nextCommon: FieldCommon) => {
      if (currentName !== undefined) {
        setObjectReview(currentName, {
          ...objectReviews[currentName],
          decisions: nextDecisions,
          field_common: nextCommon,
        });
      } else {
        setDecisions(nextDecisions);
        setFieldCommon(nextCommon);
      }
      setDirty(true);
    },
    [currentName, objectReviews, setObjectReview, setDecisions, setFieldCommon],
  );

  const setDecision = useCallback(
    (key: string, patch: Partial<FieldDecision>) => {
      const merged: FieldDecision = { ...(decisions[key] || { decision: "" }), ...patch };
      // An issue category only belongs to an incorrect field.
      const decided: FieldDecision = isIncorrectDecision(merged.decision)
        ? merged
        : { decision: merged.decision };
      const next = { ...decisions, [key]: decided };
      // Details only applies while some field (of this record / object) is incorrect.
      const common =
        !hasIncorrectDecision(next) && fieldCommon.explanation !== undefined
          ? { ...fieldCommon, explanation: undefined }
          : fieldCommon;
      writeReview(next, common);
    },
    [decisions, fieldCommon, writeReview],
  );

  const updateFieldCommon = useCallback(
    (patch: Partial<FieldCommon>) => writeReview(decisions, { ...fieldCommon, ...patch }),
    [decisions, fieldCommon, writeReview],
  );

  const setChecklistItem = useCallback(
    (itemId: string, response: ChecklistResponseValue) => {
      const next = { ...checklist, [itemId]: response };
      if (currentName !== undefined) {
        const prev = objectReviews[currentName];
        setObjectReview(currentName, {
          ...prev,
          decisions: prev?.decisions ?? {},
          checklist_responses: next,
        });
      } else {
        setChecklist(next);
      }
      setDirty(true);
    },
    [checklist, currentName, objectReviews, setObjectReview, setChecklist],
  );

  const handleCommentsChange = useCallback(
    (value: string) => {
      setComments(value);
      setDirty(true);
    },
    [setComments],
  );

  const handleFlaggedChange = useCallback(
    (value: boolean) => {
      setFlagged(value);
      setDirty(true);
    },
    [setFlagged],
  );

  const handleSubmit = () => {
    setAttemptedSubmit(true);
    if (!canSubmit) {
      // Object presence: every object must be judged. Stay on the object shown if it
      // still misses a result; otherwise show the first object that does.
      if (objectCount && currentUnit && !hasErrors(errorsOf(currentUnit))) {
        const bad = units.findIndex((u) => hasErrors(errorsOf(u)));
        if (bad >= 0) setObjectIndex(bad);
      }
      setTimeout(
        () => errorSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }),
        0,
      );
      return;
    }
    submitResult();
  };

  const requestExit = () => {
    if (submitted) {
      navigate(queuePath);
      return;
    }
    setExitOpen(true);
  };

  const saveDraftAndExit = () => {
    setExitOpen(false);
    if (!result) {
      navigate(queuePath);
      return;
    }
    setPendingExit(true);
    saveDraft();
  };

  const discardAndExit = () => {
    setExitOpen(false);
    navigate(queuePath);
  };

  // ----------------------------  Loading / Error / Not Found  ----------------------------

  if (isLoading) {
    return (
      <Row width="fill" alignmentHorizontal="center">
        <Column height="60vh" alignmentVertical="center">
          <Loader />
        </Column>
      </Row>
    );
  }

  if (isError) {
    return (
      <div style={S.pageWrapper}>
        <div
          style={{
            ...S.devBanner,
            background: "#fee2e2",
            borderColor: "#ef4444",
            color: "#991b1b",
          }}
        >
          Failed to load workspace task.
        </div>
        <button style={S.btnSecondary} onClick={() => navigate(queuePath)}>
          Back to Queue
        </button>
      </div>
    );
  }

  if (!task || !config) {
    return (
      <div style={S.pageWrapper}>
        <div
          style={{
            ...S.devBanner,
            background: "#fee2e2",
            borderColor: "#ef4444",
            color: "#991b1b",
          }}
        >
          Task not found or has no active form configuration.
        </div>
        <button style={S.btnSecondary} onClick={() => navigate(queuePath)}>
          Back to Queue
        </button>
      </div>
    );
  }

  // ----------------------------  Render  ----------------------------

  return (
    <div style={wsContainer}>
      {/* Breadcrumb */}
      <div style={wsBreadcrumb}>
        <button style={wsBreadcrumbLink} onClick={requestExit}>
          Excavator
        </button>
        <span style={wsBreadcrumbSep}>{"\u203A"}</span>
        <span style={wsBreadcrumbLink}>{config.name}</span>
        <span style={wsBreadcrumbSep}>{"\u203A"}</span>
        <span style={{ ...wsBreadcrumbLink, color: "#4b5563", fontWeight: 500 }}>
          {task.week || "Queue"}
        </span>
      </div>

      {/* Record Header */}
      <div style={wsHeader}>
        <div style={wsHeaderLeft}>
          <button
            style={{ ...wsNavBtn, opacity: prevTask ? 1 : 0.35 }}
            disabled={!prevTask}
            onClick={() => prevTask && navigate(workspacePath(prevTask.id))}
            title="Previous task"
          >
            {"\u2039"}
          </button>
          <button
            style={{ ...wsNavBtn, opacity: nextTask ? 1 : 0.35 }}
            disabled={!nextTask}
            onClick={() => nextTask && navigate(workspacePath(nextTask.id))}
            title="Next task"
          >
            {"\u203A"}
          </button>
          <h1 style={wsTitle}>Record ID: {recordId}</h1>
        </div>
        <div style={wsHeaderRight}>
          <DraftStatus dirty={dirty} lastSavedAt={lastSavedAt} submitted={submitted} />
          <button
            style={{
              ...S.btnOutline,
              padding: "10px 20px",
              fontSize: "1rem",
              opacity: submitted ? 0.5 : 1,
            }}
            disabled={submitted || isSaving}
            onClick={saveDraft}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
          <button
            style={{
              ...S.btnPrimary,
              padding: "10px 20px",
              fontSize: "1rem",
              opacity: submitted ? 0.5 : 1,
            }}
            disabled={submitted || isSaving}
            onClick={handleSubmit}
          >
            {isSaving ? "Saving..." : isLastTask ? "Submit & Finish" : "Submit & Next"}
          </button>
          <button
            style={{ ...S.btnSecondary, padding: "10px 20px", fontSize: "1rem" }}
            onClick={requestExit}
          >
            Exit
          </button>
        </div>
      </div>

      {/* Main Content — 5/12 left, 7/12 right */}
      <div style={wsBody}>
        {/* Left: Incident description + media */}
        <div style={wsLeftCol}>
          {task.incident_description && (
            <Panel title="Incident Description">
              <p style={{ fontSize: "1.125rem", color: "#374151", lineHeight: 1.6 }}>
                {task.incident_description}
              </p>
            </Panel>
          )}
          <Panel title="Media Viewer">
            {sideMedia.length > 0 ? (
              // originals beside the inspector's override / fix images
              <div
                style={{
                  ...sideBySide,
                  gridTemplateColumns: `repeat(${sideMedia.length + 1}, 1fr)`,
                }}
              >
                <div>
                  <div style={mediaHeading}>Original images</div>
                  {(task.media_urls?.length ?? 0) > 0 ? (
                    <MediaGallery media={task.media_urls!} />
                  ) : (
                    <div style={wsMediaPlaceholder}>No original images</div>
                  )}
                </div>
                {sideMedia.map((m) => (
                  <div key={m.title}>
                    <div style={mediaHeading}>{m.title}</div>
                    <MediaGallery media={m.media} />
                  </div>
                ))}
              </div>
            ) : (task.media_urls?.length ?? 0) > 0 ? (
              <MediaGallery media={task.media_urls!} />
            ) : mediaFields.length > 0 ? (
              <div style={wsMediaPlaceholder}>
                {mediaFields.map((f) => f.label).join(", ")} — no media attached
              </div>
            ) : (
              <div style={wsMediaPlaceholder}>Text-only use case</div>
            )}
          </Panel>
        </div>

        {/* Right: Fields + Checklist + Comments */}
        <div style={wsRightCol}>
          {/* Submit error */}
          {saveError && (
            <div
              style={{
                ...S.devBanner,
                background: "#fee2e2",
                borderColor: "#ef4444",
                color: "#991b1b",
                marginBottom: "16px",
              }}
            >
              Failed to save. Please try again.
            </div>
          )}

          {/* Validation error summary */}
          {attemptedSubmit && !canSubmit && (
            <div ref={errorSummaryRef} style={wsErrorSummary}>
              <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                Please resolve before submitting:
              </div>
              <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "1rem" }}>
                {fieldErrors.map((e) => (
                  <li key={e}>Needs a decision/correction: {e}</li>
                ))}
                {checklistErrors.length > 0 && (
                  <li>Checklist incomplete — mark: {checklistErrors.join(", ")}</li>
                )}
              </ul>
            </div>
          )}

          {/* Review: the additional view (a flag column is true), or the field rows */}
          <Panel title={config.name}>
            {shownAdditional && currentUnit ? (
              <>
                <ReviewHeader question={question} objectName={currentName} objectNav={objectNav} />
                <DetailRows rows={shownDetails} emptyText="No additional view rows configured." />
              </>
            ) : shownFields.length === 0 ? (
              <>
                <ReviewHeader question={question} objectName={currentName} objectNav={objectNav} />
                <p style={{ fontSize: "1.125rem", color: "#9ca3af" }}>
                  {inputFields.length === 0
                    ? "No decision fields configured."
                    : "No field has a value on this record."}
                </p>
              </>
            ) : (
              <FieldReviewTable
                fields={shownFields}
                task={task}
                values={shownValues}
                decisions={decisions}
                onDecide={setDecision}
                objectName={currentName}
                objectNav={objectNav}
                showError={attemptedSubmit}
                disabled={submitted}
              />
            )}

            {/* Details: optional note while any field is incorrect; one per object on object presence records */}
            {anyIncorrect && (
              <div style={{ marginTop: "4px" }}>
                <div style={detailsLabel}>Details</div>
                <textarea
                  rows={2}
                  value={fieldCommon.explanation ?? ""}
                  disabled={submitted}
                  onChange={(e) => updateFieldCommon({ explanation: e.target.value })}
                  placeholder="Optional: explain the evidence and the correct interpretation"
                  style={S.formTextarea}
                />
              </div>
            )}
          </Panel>

          {/* Checklist of the record / object shown, where the config shows it */}
          {showChecklist && (
            <ChecklistPanel
              items={checklistItems}
              responses={checklist}
              onPick={setChecklistItem}
              showError={attemptedSubmit}
              disabled={submitted}
            />
          )}

          {/* Comments */}
          <Panel title="Comments">
            <textarea
              rows={3}
              value={comments}
              onChange={(e) => handleCommentsChange(e.target.value)}
              placeholder="Optional reviewer comments..."
              style={S.formTextarea}
              disabled={submitted}
            />
            <label style={flagRow(submitted)}>
              <input
                type="checkbox"
                checked={flagged}
                disabled={submitted}
                onChange={(e) => handleFlaggedChange(e.target.checked)}
                style={{ width: "15px", height: "15px", accentColor: "#0f7b8a" }}
              />
              Flag this record for data or guideline follow-up
            </label>
          </Panel>
        </div>
      </div>

      {/* Status Banner */}
      {submitted && <div style={wsSuccessBanner}>This result has been submitted.</div>}

      <ExitTaskModal
        open={exitOpen}
        hasDraft={
          dirty ||
          (result?.status === "draft" &&
            (Object.keys(result.decisions).length > 0 ||
              Object.keys(result.object_reviews ?? {}).length > 0))
        }
        onSaveAndExit={saveDraftAndExit}
        onDiscardAndExit={discardAndExit}
        onCancel={() => setExitOpen(false)}
      />
    </div>
  );
};

// ----------------------------  Styles  ----------------------------

const detailsLabel: React.CSSProperties = {
  fontSize: "1.2rem",
  fontWeight: 600,
  color: "#6b7280",
  marginBottom: "4px",
};

const sideBySide: React.CSSProperties = {
  display: "grid",
  gap: "12px",
  alignItems: "start",
};

const mediaHeading: React.CSSProperties = {
  fontSize: "1.2rem",
  fontWeight: 600,
  color: "#6b7280",
  marginBottom: "6px",
};

const flagRow = (disabled: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "10px",
  cursor: disabled ? "default" : "pointer",
  fontSize: "1.1rem",
  color: "#374151",
});

export default AuditWorkspace;
